import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { getDocumentIdToQueueItemMap, getRejectionsByDocumentId } from '@/helpers/queues';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetPerformProgressQuery } from '@/services/organizations';
import {
  Box,
  Button,
  Card,
  Modal,
  SelectInput,
  Spinner,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  ActivationDocumentTypes,
  AllDocumentTypes,
  DocumentDocument,
  DocumentStatus,
  documentTitles,
  DocumentTypes,
  DoePrDocumentTypes,
  HVACDocumentTypes,
  InstallDocumentTypes,
  ProgramType,
  UserPermissions,
} from 'types';

import { useUploadDocumentMutation } from '../services/documents';
import { useGetQueuesByAccountIdQuery } from '../services/queues';
import { useGetTasksQuery } from '../services/tasks';

import { useIsOrgReadOnly } from './auth/claimsUtils';
import RequirePermissions from './auth/requirePermissions';
import { DocumentListItem } from './documents/DocumentListItem';

type DocumentsProps = {
  documentTypeFilter: Array<AllDocumentTypes> | undefined;
  additionalDocumentsToFetch?: Array<{
    types: Array<AllDocumentTypes> | undefined;
    status?: Array<DocumentStatus>;
  }>;
  showDocumentActions?: boolean;
  allowArchive?: boolean;
  showUploadButton?: boolean;
  className?: string;
  title?: string;
  originContext?: string;
  programType?: string;
};

export function Documents({
  documentTypeFilter = [],
  additionalDocumentsToFetch,
  showDocumentActions = true,
  allowArchive = true,
  showUploadButton = true,
  className,
  title,
  originContext,
  programType,
}: DocumentsProps) {
  const { id } = useParams<{ id: any }>();
  const queryParams = new URLSearchParams(useLocation().search);
  const scrollTo = queryParams.get('scrollTo');

  // Single subscription — all useGetDocumentsQuery calls share the same { accountId, v2 } cache key,
  // so we subscribe once and filter client-side instead of calling hooks in a loop. Going through
  // useGetDocumentsQuery (rather than the raw endpoint) injects the documents-v2 flag, so view URLs
  // resolve to signed GCS URLs when v2 is enabled instead of legacy streaming-proxy app URLs.
  const { data, isLoading: isDocumentsLoading } = useGetDocumentsQuery({ accountId: id }, { skip: !id });
  const allAccountDocuments: DocumentDocument[] = data ?? [];

  const documents = useMemo(() => {
    if (!allAccountDocuments.length) return [];

    const primaryTypeSet = documentTypeFilter ? new Set(documentTypeFilter) : null;
    const primaryDocs = primaryTypeSet
      ? allAccountDocuments.filter((doc) => primaryTypeSet.has(doc.type))
      : allAccountDocuments;

    if (!additionalDocumentsToFetch?.length) return primaryDocs;

    const additionalDocs = additionalDocumentsToFetch.flatMap(({ types, status }) => {
      const typeSet = types ? new Set(types) : null;
      const statusSet = status ? new Set(status) : null;
      return allAccountDocuments.filter((doc) => {
        if (typeSet && !typeSet.has(doc.type)) return false;
        if (statusSet && !statusSet.has(doc.status as DocumentStatus)) return false;
        return true;
      });
    });

    // Combine and de-duplicate by id
    const docMap = new Map();
    [...primaryDocs, ...additionalDocs].forEach((doc) => {
      if (doc.id) docMap.set(doc.id.toString(), doc);
    });
    return Array.from(docMap.values());
  }, [allAccountDocuments, documentTypeFilter, additionalDocumentsToFetch]);
  const { data: tasks = [], isLoading: isTasksLoading } = useGetTasksQuery(
    { id, assignee: 'internal' },
    { skip: !id },
  );
  const { isPhone } = useBreakpoint();
  const { isOpen: isUploadModal, handleClose: handleUploadModalClose, handleOpen: openUploadModal } = useOpenClose();

  const [uploadDocument] = useUploadDocumentMutation();
  const allowsAction = useIsOrgReadOnly();

  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);
  const isLoading = isDocumentsLoading || isTasksLoading;

  const sectionRefs = useMemo(
    () => ({
      documents: createRef(),
    }),
    [],
  ) as any;

  useEffect(() => {
    if (scrollTo) {
      sectionRefs[scrollTo]?.current?.scrollIntoView();
    }
  }, [scrollTo, sectionRefs]);

  // These values must match the DocumentTypes enum in the API.
  const documentTypeOptions = useMemo(() => {
    const allDocuments = {
      ...DocumentTypes,
      ...InstallDocumentTypes,
      ...ActivationDocumentTypes,
      ...HVACDocumentTypes,
      ...DoePrDocumentTypes,
    };
    const documentTypes = Object.keys(allDocuments)
      .map((key) => {
        return { value: key, label: documentTitles[key as AllDocumentTypes] };
      })
      .filter(
        (dt) => (programType && programType === ProgramType.newCommunity) || dt.value !== DocumentTypes.contract,
      ); // Want to fetch contract-type documents but not allow upload, except for New Community program.
    if (documentTypeFilter.length > 0) {
      return documentTypes.filter((dt) => documentTypeFilter.includes(dt.value as AllDocumentTypes));
    }

    return documentTypes;
  }, [documentTypeFilter, programType]);

  const [files, setFiles] = useState<FileList | undefined>();

  const [type, setType] = useState<{ value: string; label: string } | undefined>();

  useEffect(() => {
    if (documentTypeOptions.length === 1) {
      setType(documentTypeOptions[0]);
    }
  }, [documentTypeOptions, setType]);

  const onFileChange = useCallback(
    (event: any) => {
      setFiles(event.target?.files);
    },
    [setFiles],
  );

  const closeUploadModal = useCallback(() => {
    setFiles(undefined);
    handleUploadModalClose();
  }, [setFiles, handleUploadModalClose]);

  const onTypeChange = useCallback((event: any) => setType(event.target.value), [setType]);

  const handleUpload = useCallback(() => {
    if (!files || !files.length) return;

    const data = new FormData();
    data.append('grouped', 'true');

    if (type && 'value' in type) {
      data.append('type', type.value);
    }
    for (let i = 0; i < files.length; i++) {
      data.append(`files[${i}]`, files[i]);
    }
    if (originContext) {
      data.append('context', originContext);
    }

    closeUploadModal();

    uploadDocumentsWithToast({
      uploadDocument,
      accountId: id,
      formData: data,
      invalidateAllTags: true,
      files,
    });
  }, [files, type, originContext, id, uploadDocument, closeUploadModal]);

  return (
    <>
      <Modal isOpen={isUploadModal} onDismiss={closeUploadModal} maxWidth="4xl" ariaLabelledBy="uploadDocHeader">
        <Modal.Header id="uploadDocHeader" title="Upload Documents" onDismiss={closeUploadModal} />
        <Modal.Body background="secondary" childGap="lg">
          <SelectInput
            size="md"
            id="required"
            label="Document Type"
            value={type}
            onChange={onTypeChange}
            options={documentTypeOptions}
            isRequired
            isDisabled={documentTypeOptions.length === 1}
            menuPortalTarget={document.body}
          />
          <ResettingFileUpload
            buttonText="Choose File(s)"
            name="files"
            labelText="Choose File(s)"
            id="files"
            variant="secondary"
            tone="neutral"
            multiple
            required
            requiredIndicator=""
            onChange={onFileChange}
            files={files}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" isDisabled={isLoading} onClick={closeUploadModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpload} isDisabled={!files || !files.length}>
            Upload Document
          </Button>
        </Modal.Footer>
      </Modal>
      <Card className={className}>
        <Box
          childGap="lg"
          padding="lg"
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          ref={sectionRefs.documents}
        >
          <Box as="h3" fontWeight="medium" fontSize="md">
            {title ? `${title} Documents` : 'Documents'} {documents?.length > 0 && `(${documents?.length})`}
          </Box>
          {showUploadButton && (
            <RequirePermissions
              permissions={[UserPermissions.admin, UserPermissions.editor]}
              checkAllPermissions={false}
            >
              <Button
                disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction}
                size="sm"
                variant="primary"
                tone="neutral"
                iconPrefix="upload"
                onClick={openUploadModal}
              >
                {!isPhone && 'Upload'}
              </Button>
            </RequirePermissions>
          )}
        </Box>
        <DocumentList
          isLoading={isLoading}
          items={documents as any}
          tasks={tasks}
          showDocumentActions={showDocumentActions}
          allowArchive={allowArchive}
        />
      </Card>
    </>
  );
}
export interface DocumentsListProps {
  isLoading: boolean;
  items: any[];
  tasks: any[];
  showDocumentActions?: boolean;
  allowArchive?: boolean;
}

export function DocumentList({
  isLoading,
  items = [],
  tasks = [],
  showDocumentActions = true,
  allowArchive = true,
}: DocumentsListProps) {
  const totalCount = items?.length || 0;
  const { id } = useParams<{ id: any }>();
  const { data: queueItems } = useGetQueuesByAccountIdQuery({
    accountId: id,
  });
  const documentIdToQueueItemMap = useMemo(() => getDocumentIdToQueueItemMap(queueItems), [queueItems]);
  const rejectionsByDocumentId = useMemo(() => getRejectionsByDocumentId(queueItems), [queueItems]);

  return (
    <>
      <Card.Section padding="0">
        {totalCount > 0 ? (
          items.map((item) => {
            const task = tasks.find((task) => {
              const documentReference = task.referenceIds.find((reference: any) => reference.type === 'documents');
              return documentReference?.id === item?.id;
            });
            const key = item.id?.toString?.() ?? '';
            const queueItem = documentIdToQueueItemMap[key];
            const rejections = rejectionsByDocumentId.get(key) ?? [];
            return (
              <DocumentListItem
                item={item}
                key={item.id}
                showDocumentActions={showDocumentActions}
                allowArchive={allowArchive}
                task={task}
                queue={queueItem}
                rejections={rejections}
              />
            );
          })
        ) : (
          <Box
            display="block"
            textAlign="center"
            padding="lg"
            childGap="xl"
            borderColor="separator"
            borderWidth="xs 0 0 0"
          >
            {isLoading ? (
              <Spinner size="lg" />
            ) : (
              <>
                <Box fontSize="sm" color="body-secondary">
                  No documents exist for this account
                </Box>
              </>
            )}
          </Box>
        )}
      </Card.Section>
    </>
  );
}
